/**
 * Company Details Modal
 * Shows company information and statistics
 */
import React from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Card,
  Button,
} from 'antd';
import {
  ApartmentOutlined,
  TeamOutlined,
  ShopOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Company, CompanyStats } from '../../services/superAdminService';
import '../../styles/modalStyles.css';

const { Title } = Typography;

interface CompanyDetailsModalProps {
  visible: boolean;
  company: Company | null;
  stats: CompanyStats | null;
  sidebarCollapsed?: boolean;
  onCancel: () => void;
}

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
  visible,
  company,
  stats,
  sidebarCollapsed = false,
  onCancel,
}) => {
  if (!company) return null;

  const subscriptionStatus = company.subscription_expires_at
    ? (() => {
        const expiryDate = new Date(company.subscription_expires_at);
        const now = new Date();
        const daysRemaining = Math.ceil(
          (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        return {
          isExpired: daysRemaining < 0,
          daysRemaining: Math.max(0, daysRemaining),
          expiresAt: expiryDate,
        };
      })()
    : null;

  return (
    <Modal
      title={
        <Space>
          <ApartmentOutlined />
          <span>Company Details - {company.name}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      getContainer={() => document.querySelector('.main-content-full') || document.body}
      wrapClassName="form-panel-full-wrap"
      width="100%"
      style={{ maxWidth: 'none' }}
      destroyOnClose
      zIndex={2000}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button type="primary" onClick={onCancel}>Close</Button>
        </div>
      }
      bodyStyle={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Company Information */}
        <div>
          <Title level={5}>Company Information</Title>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Name">{company.name}</Descriptions.Item>
            <Descriptions.Item label="Code">{company.code}</Descriptions.Item>
            <Descriptions.Item label="Country">{company.country || '-'}</Descriptions.Item>
            <Descriptions.Item label="Corporate Residence">
              {company.corporate_residence || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">{company.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{company.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>
              {company.address || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Registration Number">
              {company.registration_number || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tax ID">{company.tax_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Plan Type">
              <Tag color={company.plan_type === 'premium' ? 'gold' : company.plan_type === 'enterprise' ? 'purple' : 'blue'}>
                {(company.plan_type || 'basic') === 'basic' ? 'STANDARD' : (company.plan_type || '').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="License Type">
              {company.license_type ? (
                <Tag color={company.license_type === 'macro' ? 'cyan' : 'geekblue'}>
                  {company.license_type.toUpperCase()}
                </Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={company.is_active ? 'green' : 'red'}>
                {company.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Subscription Information */}
        <div>
          <Title level={5}>Subscription Information</Title>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Max Users">{company.max_users}</Descriptions.Item>
            <Descriptions.Item label="Max Locations">{company.max_locations}</Descriptions.Item>
            <Descriptions.Item label="Expires At">
              {company.subscription_expires_at
                ? new Date(company.subscription_expires_at).toLocaleDateString()
                : 'No expiration'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {subscriptionStatus ? (
                <Tag color={subscriptionStatus.isExpired ? 'red' : subscriptionStatus.daysRemaining < 30 ? 'orange' : 'green'}>
                  {subscriptionStatus.isExpired
                    ? 'Expired'
                    : `${subscriptionStatus.daysRemaining} days remaining`}
                </Tag>
              ) : (
                <Tag color="green">No expiration</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Statistics */}
        {stats && (
          <div>
            <Title level={5}>Company Statistics</Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Users"
                    value={stats.total_users}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Active Users"
                    value={stats.active_users}
                    prefix={<TeamOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Locations"
                    value={stats.total_locations}
                    prefix={<ShopOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Active Locations"
                    value={stats.active_locations}
                    prefix={<ShopOutlined />}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Total Products"
                    value={stats.total_products}
                    prefix={<TagsOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Purchase Orders"
                    value={stats.total_purchase_orders}
                    prefix={<ShoppingCartOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Total Sales"
                    value={stats.total_sales}
                    formatter={(value) => `Nu. ${Number(value || 0).toLocaleString()}`}
                  />
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default CompanyDetailsModal;
